import React, { useState } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAuth } from "../../hooks/useAuth";

const UserCertification = ({ show = true, onHide = () => {} }) => {
  const { backendUrl, token } = useAuth();

  const [formData, setFormData] = useState({
    Staff_ID: "",
    certificationId: "",
    expiresAt: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [actionError, setActionError] = useState("");
  const queryClient = useQueryClient();

  const extractResponseData = payload => {
    if (payload && typeof payload === "object" && "data" in payload) {
      return payload.data;
    }
    return payload;
  };

  const toDatetimeLocalValue = value => {
    if (!value) {
      return "";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const timezoneOffsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - timezoneOffsetMs)
      .toISOString()
      .slice(0, 16);
  };

  const fetchCertifications = async () => {
    const response = await fetch(`${backendUrl}/api/user-certifications`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch user certifications.");
    }

    const payload = await response.json();
    const data = extractResponseData(payload);
    return Array.isArray(data) ? data : [];
  };

  const { data: certifications = [], isLoading, isFetching, error } = useQuery(
    ["user-certifications"],
    fetchCertifications,
    {
      enabled: show,
      refetchOnWindowFocus: false,
    }
  );

  const createMutation = useMutation(
    async payload => {
      const response = await fetch(`${backendUrl}/api/user-certifications`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to create user certification.");
      }

      const payloadResponse = await response.json();
      return extractResponseData(payloadResponse);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["user-certifications"]);
        resetForm();
        setActionError("");
      },
      onError: mutationError => {
        setActionError(
          mutationError?.message || "Could not create user certification."
        );
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, payload }) => {
      const response = await fetch(
        `${backendUrl}/api/user-certifications/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update user certification.");
      }

      const payloadResponse = await response.json();
      return extractResponseData(payloadResponse);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["user-certifications"]);
        resetForm();
        setActionError("");
      },
      onError: mutationError => {
        setActionError(
          mutationError?.message || "Could not update user certification."
        );
      },
    }
  );

  const deleteMutation = useMutation(
    async id => {
      const response = await fetch(
        `${backendUrl}/api/user-certifications/${id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete user certification.");
      }

      return id;
    },
    {
      onMutate: id => {
        setDeletingId(id);
      },
      onSuccess: () => {
        queryClient.invalidateQueries(["user-certifications"]);
        setActionError("");
      },
      onError: mutationError => {
        setActionError(
          mutationError?.message || "Could not delete user certification."
        );
      },
      onSettled: () => {
        setDeletingId(null);
      },
    }
  );

  const handleCreate = async e => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  const handleUpdate = async id => {
    updateMutation.mutate({ id, payload: formData });
  };

  const handleDelete = async id => {
    deleteMutation.mutate(id);
  };

  const handleEdit = cert => {
    setEditingId(cert.id);
    setFormData({
      Staff_ID: cert.Staff_ID,
      certificationId: cert.certificationId,
      expiresAt: toDatetimeLocalValue(cert.expiresAt),
    });
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({ Staff_ID: "", certificationId: "", expiresAt: "" });
    setActionError("");
  };

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;
  const isDeleting = deleteMutation.isLoading;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>User Certification</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form
          onSubmit={
            editingId
              ? e => {
                  e.preventDefault();
                  handleUpdate(editingId);
                }
              : handleCreate
          }
          className="mb-4"
        >
          {!!actionError && (
            <Alert variant="danger" className="mb-3">
              {actionError}
            </Alert>
          )}

          <Row className="g-3 align-items-end">
            <Col md={4}>
              <Form.Group controlId="userCertificationStaffId">
                <Form.Label>Staff ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter staff ID"
                  value={formData.Staff_ID}
                  onChange={e =>
                    setFormData({ ...formData, Staff_ID: e.target.value })
                  }
                  disabled={isSubmitting}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="userCertificationId">
                <Form.Label>Certification ID</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter certification ID"
                  value={formData.certificationId}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      certificationId: e.target.value,
                    })
                  }
                  disabled={isSubmitting}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group controlId="userCertificationExpiresAt">
                <Form.Label>Expires At</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={formData.expiresAt}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      expiresAt: e.target.value,
                    })
                  }
                  disabled={isSubmitting}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={12} className="d-grid d-md-flex justify-content-md-end">
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : editingId ? (
                  "Update"
                ) : (
                  "Add"
                )}
              </Button>
            </Col>
          </Row>

          {editingId && (
            <div className="mt-3">
              <Button
                type="button"
                variant="outline-secondary"
                onClick={resetForm}
                disabled={isSubmitting}
              >
                Cancel Edit
              </Button>
            </div>
          )}
        </Form>

        {isLoading ? (
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" />
            <span>Loading user certifications...</span>
          </div>
        ) : error ? (
          <Alert variant="danger" className="mb-0">
            {error?.message || "Error loading user certifications."}
          </Alert>
        ) : (
          <>
            {isFetching && (
              <div className="d-flex align-items-center gap-2 mb-3">
                <Spinner animation="border" size="sm" />
                <span>Refreshing data...</span>
              </div>
            )}

            <Table
              striped
              borderless
              hover
              responsive
              className="product-table mb-0"
            >
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Staff ID</th>
                  <th>Certification ID</th>
                  <th>Expires At</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {certifications.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      No user certifications found.
                    </td>
                  </tr>
                )}

                {certifications.map(cert => (
                  <tr key={cert.id}>
                    <td>{cert.id}</td>
                    <td>{cert.Staff_ID}</td>
                    <td>{cert.certificationId}</td>
                    <td>{new Date(cert.expiresAt).toLocaleString()}</td>
                    <td>{new Date(cert.created_at).toLocaleDateString()}</td>
                    <td className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="warning"
                        onClick={() => handleEdit(cert)}
                        disabled={isSubmitting || isDeleting}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => handleDelete(cert.id)}
                        disabled={isSubmitting || isDeleting}
                      >
                        {deletingId === cert.id ? "Deleting..." : "Delete"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserCertification;
